import "../tests/exam/components/Layout/Footer.css";
import Button from "../../shared/components/Button/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  saveAnswerRequest,
  setCurrentQuestionIndex,
  setActiveSection,
  updateQuestionStatus,
  hidePracticeProgressiveMessage,
} from "../../redux/exam/examSlice";
import {
  getBackendQuestionStatus,
  getExamSaveAnswerFields,
  getQuestionIntegerAnswer,
  isIntegerTypeQuestion,
} from "../../shared/utilities/helpers/examQuestionStatus";
import { getExamSectionKey } from "../../shared/utilities/helpers/examSectionKey";

/**
 * Mistake Book footer — single navigation path.
 * Shared Footer ignores parent onSaveNext and saves without skipAdvance,
 * which double-fires exam_single_question (saga + alignment effect).
 * Here: save with skipAdvance, advance index only; parent loads the question once.
 */
const MistakeBookTestFooter = () => {
  const dispatch = useDispatch();
  const {
    currentQuestionIndex,
    questionsData,
    testDetails,
    paperId,
    testType,
    loading,
  } = useSelector((state) => state.examSlice);

  const currentQuestion = questionsData?.[currentQuestionIndex];
  const isPracticeMode = testDetails?.practiceMode === "practice";
  const isPracticeGamified =
    isPracticeMode && testDetails?.gamificationOn === true;
  const isLastQuestion =
    currentQuestionIndex === (questionsData?.length || 0) - 1;

  const hasCurrentAnswer = () => {
    if (!currentQuestion) return false;
    if (isIntegerTypeQuestion(currentQuestion)) {
      return getQuestionIntegerAnswer(currentQuestion).trim() !== "";
    }
    const answerType = Number(currentQuestion?.answer_type_id);
    return answerType === 2
      ? Array.isArray(currentQuestion?.selectedOption) &&
          currentQuestion.selectedOption.length > 0
      : currentQuestion?.selectedOption !== null &&
          currentQuestion?.selectedOption !== undefined &&
          currentQuestion?.selectedOption !== "";
  };

  const advanceToIndex = (nextIndex, fromQuestion) => {
    if (nextIndex < 0 || nextIndex >= (questionsData?.length || 0)) return;
    const nextQuestion = questionsData[nextIndex];
    if (!nextQuestion) return;

    if (
      getExamSectionKey(nextQuestion) !== getExamSectionKey(fromQuestion)
    ) {
      dispatch(setActiveSection(getExamSectionKey(nextQuestion)));
    }
    dispatch(setCurrentQuestionIndex(nextIndex));
  };

  const handleSaveNext = (e) => {
    e.preventDefault();
    if (loading || !currentQuestion) return;

    const isIntegerQuestion = isIntegerTypeQuestion(currentQuestion);
    const hasSelectedMcq =
      Number(currentQuestion?.answer_type_id) === 2
        ? Array.isArray(currentQuestion?.selectedOption) &&
          currentQuestion.selectedOption.length > 0
        : currentQuestion?.selectedOption !== null &&
          currentQuestion?.selectedOption !== undefined &&
          currentQuestion?.selectedOption !== "";
    const hasCheckedInteger = currentQuestion?.integerChecked === true;

    const triggerStreak = !isPracticeGamified
      ? true
      : isIntegerQuestion
        ? !hasCheckedInteger
        : !hasSelectedMcq;

    if (isPracticeGamified) {
      dispatch(hidePracticeProgressiveMessage());
    }

    dispatch(
      saveAnswerRequest({
        paper_type: testDetails?.paperType || testDetails?.paper_type || testType?.id,
        paper_id: currentQuestion?.paper_id || paperId,
        qid: currentQuestion?.qid,
        test_id: testDetails?.testId,
        set_id: testDetails?.setId,
        time: currentQuestion?.time ?? currentQuestion?.time_taken ?? 0,
        triggerStreak,
        question_status: getBackendQuestionStatus(currentQuestion),
        ...getExamSaveAnswerFields(currentQuestion),
        skipAdvance: true,
      })
    );

    if (isLastQuestion) {
      dispatch(
        updateQuestionStatus({
          qid: currentQuestion.qid,
          moveAway: true,
        })
      );
      return;
    }

    advanceToIndex(currentQuestionIndex + 1, currentQuestion);
  };

  const handlePrevious = () => {
    if (currentQuestionIndex === 0 || !currentQuestion) return;
    advanceToIndex(currentQuestionIndex - 1, currentQuestion);
  };

  const handleMarkReview = () => {
    if (loading || !currentQuestion) return;

    const markedQuestion = { ...currentQuestion, marked: true };
    dispatch(
      updateQuestionStatus({
        qid: currentQuestion.qid,
        markReview: true,
        ...(isLastQuestion ? { moveAway: true } : {}),
      })
    );

    dispatch(
      saveAnswerRequest({
        paper_type: testDetails?.paperType || testDetails?.paper_type || testType?.id,
        paper_id: currentQuestion?.paper_id || paperId,
        qid: currentQuestion?.qid,
        test_id: testDetails?.testId,
        set_id: testDetails?.setId,
        time: currentQuestion?.time ?? currentQuestion?.time_taken ?? 0,
        question_status: getBackendQuestionStatus(markedQuestion),
        ...getExamSaveAnswerFields(currentQuestion),
        skipAdvance: true,
      })
    );

    if (!isLastQuestion) {
      advanceToIndex(currentQuestionIndex + 1, currentQuestion);
    }
  };

  const handlePracticeAction = () => {
    if (!currentQuestion) return;
    const alreadyChecked = currentQuestion?.answerChecked === true;
    const isIntegerQuestion = isIntegerTypeQuestion(currentQuestion);

    if (alreadyChecked) {
      dispatch(
        updateQuestionStatus({
          qid: currentQuestion.qid,
          showSolution: true,
        })
      );
      return;
    }

    if (!hasCurrentAnswer()) return;

    dispatch(
      updateQuestionStatus({
        qid: currentQuestion.qid,
        answerChecked: true,
        showSolution: false,
        integerChecked: isIntegerQuestion
          ? true
          : currentQuestion?.integerChecked,
      })
    );

    dispatch(
      saveAnswerRequest({
        paper_type: testDetails?.paperType || testDetails?.paper_type || testType?.id,
        paper_id: currentQuestion?.paper_id || paperId,
        qid: currentQuestion?.qid,
        test_id: testDetails?.testId,
        set_id: testDetails?.setId,
        time: currentQuestion?.time ?? currentQuestion?.time_taken ?? 0,
        triggerStreak: isPracticeGamified,
        question_status: getBackendQuestionStatus(currentQuestion),
        ...getExamSaveAnswerFields(currentQuestion),
        skipAdvance: true,
      })
    );
  };

  const handleViewSolution = () => {
    if (!currentQuestion || !hasCurrentAnswer()) return;
    dispatch(
      updateQuestionStatus({
        qid: currentQuestion.qid,
        showSolution: true,
      })
    );
  };

  return (
    <div className="footer-container">
      <div className="footer-left">
        <Button
          title="Previous"
          variant="outlined"
          color="primary"
          className="footer-btn outlined-btn"
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
        />
        {isPracticeGamified ? (
          <Button
            title="View Solution"
            variant="outlined"
            color="primary"
            className="footer-btn outlined-btn"
            onClick={handleViewSolution}
            disabled={!hasCurrentAnswer()}
          />
        ) : isPracticeMode ? (
          <Button
            title={
              currentQuestion?.answerChecked ? "View Solution" : "Check Answer"
            }
            variant="outlined"
            color="primary"
            className="footer-btn outlined-btn"
            onClick={handlePracticeAction}
            disabled={!currentQuestion?.answerChecked && !hasCurrentAnswer()}
          />
        ) : (
          <Button
            title="Mark for Review"
            variant="outlined"
            color="primary"
            className="footer-btn outlined-btn"
            onClick={handleMarkReview}
          />
        )}
      </div>

      <div className="footer-right">
        <Button
          variant="contained"
          color="error"
          title={isLastQuestion ? "Save" : "Save & Next"}
          className="footer-btn contained-error"
          onClick={handleSaveNext}
          disabled={loading || !hasCurrentAnswer()}
        />
      </div>
    </div>
  );
};

export default MistakeBookTestFooter;
